import { HelpCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';

export interface ReferentialStat {
  key: string;
  /** Short label, top of the card. */
  label: string;
  /** Null when the figure was never measured — the card reads `—` rather than a zero. */
  value: number | null;
  icon: LucideIcon;
  /** One line under the figure: what it counts, in business terms. */
  /** Optional: a card whose figure could not be read must not caption it either. */
  hint?: string;
  /** Longer explanation on hover — for a counter whose rule is not obvious. */
  tooltip?: string;
}

export interface ReferentialStatsProps {
  stats: ReferentialStat[];
  loading?: boolean;
}

/**
 * The four counters both referential screens open with, in the shape the other admin screens use
 * (title + icon on top, figure, one hint line) — the mock-up switched to an `auto-fit` grid here
 * alone, which is an inconsistency, not a spec.
 */
const ReferentialStats = ({ stats, loading = false }: ReferentialStatsProps) => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
    {stats.map((stat) => {
      const Icon = stat.icon;
      return (
        <Card key={stat.key}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
              {stat.label}
              {stat.tooltip && (
                // Native tooltip on purpose: the styled Radix one needs a provider, and coupling
                // a stat card to it would crash any tree that renders without one. A button, not
                // a bare icon, so the hint is reachable by keyboard and read out loud.
                <button
                  type="button"
                  title={stat.tooltip}
                  aria-label={stat.tooltip}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
              )}
            </CardTitle>
            <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-12" />
            ) : (
              <div className="text-2xl font-bold tabular-nums">{stat.value ?? '—'}</div>
            )}
            {stat.hint && <p className="mt-1 text-xs text-muted-foreground">{stat.hint}</p>}
          </CardContent>
        </Card>
      );
    })}
  </div>
);

export default ReferentialStats;
